import {
  ChangeRequestType,
  getTranslatedDDSLine,
  type KeyValueType
} from 'de-zurich-ui-library'
import { t } from '@/i18n'
import type { Policy } from 'policies-api'
import {
  getCustomComponentNameFromDdsLines,
  TagMap
} from '@/utilsTenant/DynamicPolicyTileMapping'
import { isActivePolicyStatus } from './PolicyUtils'
import { PaymentMethod } from 'policies-api/dist/models/PaymentMethod'

export class PolicyList {
  protected static readonly POLICY_BUS_TIMEOUT_MS = 15000

  public static getFormBlocks(
    policies: Policy[],
    localeFilename: string
  ): KeyValueType[][] {
    const blocks = []
    for (const policy of policies) {
      const block: KeyValueType[] = []
      // default policy blocks
      const ddsLine = policy?.ddsLines === undefined ? '' : policy.ddsLines[0]!
      block.push({
        key: getTranslatedDDSLine(ddsLine),
        value: 'Versicherungsnummer ' + policy.policyNumber,
        type: 'policy'
      })
      // individual blocks
      const listBlocks: KeyValueType[] = PolicyList.getListBlocks(policy, localeFilename)
      block.push({
        key: listBlocks[0]!.key + ' ' + listBlocks[0]!.value,
        value: listBlocks[1]!.key + ' ' + listBlocks[1]!.value,
        type: 'info'
      })
      blocks.push(block)
    }
    return blocks
  }

  public static getListBlocks(policy: Policy, localeFilename: string): KeyValueType[] {
    const text: KeyValueType[] = []
    const getIbanValue = (): string => {
      const { paymentData } = policy
      const noData = t(localeFilename + '.blocks.noData')

      if (!paymentData?.paymentProcedure) return noData

      if ('iban' in paymentData.paymentProcedure && paymentData.paymentProcedure.iban) {
        return paymentData.paymentProcedure.iban as string
      }

      if (paymentData.paymentMethod === PaymentMethod.Invoice) {
        return t(localeFilename + '.blocks.invoicePolicy')
      }

      return noData
    }

    text.push(
      {
        key: t(localeFilename + '.blocks.iban'),
        value: getIbanValue(),
        type: ''
      },
      {
        key: t(localeFilename + '.blocks.accountHolder'),
        value:
          policy.paymentData?.paymentProcedure &&
          'cardHolderName' in policy.paymentData.paymentProcedure &&
          policy.paymentData.paymentProcedure.cardHolderName
            ? (policy.paymentData.paymentProcedure.cardHolderName as string)
            : t(localeFilename + '.blocks.noData'),
        type: ''
      }
    )
    return text
  }

  public static modifyPolicy(policy: Policy): Policy {
    const tempPolicy = policy
    if (
      PolicyList.checkPolicyDetails(policy) &&
      tempPolicy.paymentData?.paymentProcedure &&
      'iban' in tempPolicy.paymentData.paymentProcedure &&
      'cardHolderName' in tempPolicy.paymentData.paymentProcedure
    ) {
      tempPolicy.paymentData.paymentProcedure.iban = this.getFormattedIban(policy)
      tempPolicy.paymentData.paymentProcedure.cardHolderName =
        this.getFormattedCardHolderName(policy)
    }
    return tempPolicy
  }

  public static checkPoliciesDetails(
    policies: Policy[],
    blackListedPolicyIDs: Set<string>
  ): boolean {
    return policies.some((policy) =>
      this.checkPolicyOverview(policy, blackListedPolicyIDs)
    )
  }

  public static checkPolicyOverview(
    policy: Policy,
    blackListedPolicyIDs: Set<string>
  ): boolean {
    // has a policyId and policyId is NOT blacklisted
    return policy?.policyId !== null && !blackListedPolicyIDs.has(policy.policyId)
  }

  public static checkPolicyDetails(policy: Policy): boolean {
    return !!policy.paymentData
  }

  public static getChangeRequestType(): ChangeRequestType {
    return ChangeRequestType.BankData
  }

  // this function returns true if a policy is valid to be shown
  public static filterValidToBeShown(policy: Policy): boolean {
    return this.isPolicyActive(policy) && this.isPolicyDomainAndTagNameValid(policy)
  }

  public static isPolicyActive(policy: Policy): boolean {
    // NON_CONTRIBUTORY_B and NON_CONTRIBUTORY_C should be treated as active
    // according to KPTWO-5823: These statuses should be counted as active and styled accordingly
    return isActivePolicyStatus(policy.policyStatus)
  }

  public static isPolicyDomainAndTagNameValid(policy: Policy): boolean {
    const policyDomain = getCustomComponentNameFromDdsLines(policy.ddsLines ?? [])
    const tagName = policyDomain ? TagMap[policyDomain] : undefined
    return tagName != undefined
  }

  private static getFormattedIban(policy: Policy): string {
    // insert a space every 4 characters
    return policy.paymentData &&
      policy.paymentData.paymentProcedure &&
      'iban' in policy.paymentData.paymentProcedure
      ? (policy.paymentData.paymentProcedure.iban as string)
          .replaceAll(/[^\dA-Z*]/g, '')
          .replaceAll(/(.{4})/g, '$1 ')
          .trim()
      : ''
  }

  private static getFormattedCardHolderName(policy: Policy): string {
    const splitName: string[] =
      policy.paymentData &&
      policy.paymentData.paymentProcedure &&
      'cardHolderName' in policy.paymentData.paymentProcedure
        ? (policy.paymentData.paymentProcedure.cardHolderName as string).split(', ')
        : []
    splitName.reverse()
    return splitName.length > 0 ? splitName.join(' ') : ''
  }
}
